// Tracks Mozc's per-segment conversion state so the customer can convert one
// phrase at a time within a longer string, instead of one flat result.
import { getMozcSegments, HechimaSegment } from '../mozcWasm';

class SegmentComposer {
  static segments: HechimaSegment[] = [];

  static selections: number[] = [];

  static activeIndex = 0;

  static async setReading(reading: string): Promise<void> {
    if (!reading) {
      SegmentComposer.reset();
      return;
    }
    const segments = await getMozcSegments(reading);
    SegmentComposer.segments = segments && segments.length > 0 ? segments : [{ key: reading, candidates: [reading] }];
    // -1 = not converted yet: show the raw typed reading (underlined "draft"),
    // matching how a real IME behaves, until the customer picks a candidate.
    SegmentComposer.selections = SegmentComposer.segments.map(() => -1);
    SegmentComposer.activeIndex = SegmentComposer.segments.length - 1;
  }

  static reset(): void {
    SegmentComposer.segments = [];
    SegmentComposer.selections = [];
    SegmentComposer.activeIndex = 0;
  }

  static hasSegments(): boolean {
    return SegmentComposer.segments.length > 0;
  }

  static nextSegment(): void {
    if (SegmentComposer.activeIndex < SegmentComposer.segments.length - 1) {
      SegmentComposer.activeIndex += 1;
    }
  }

  static prevSegment(): void {
    if (SegmentComposer.activeIndex > 0) {
      SegmentComposer.activeIndex -= 1;
    }
  }

  static cycleCandidate(): void {
    const segment = SegmentComposer.segments[SegmentComposer.activeIndex];
    if (!segment || segment.candidates.length === 0) {
      return;
    }
    const current = SegmentComposer.selections[SegmentComposer.activeIndex] ?? -1;
    SegmentComposer.selections[SegmentComposer.activeIndex] = (current + 1) % segment.candidates.length;
  }

  static selectCandidate(index: number): void {
    SegmentComposer.selections[SegmentComposer.activeIndex] = index;
  }

  static getActiveCandidates(): string[] {
    return SegmentComposer.segments[SegmentComposer.activeIndex]?.candidates ?? [];
  }

  // Index of the currently selected candidate within the active segment, or -1
  // if the segment is still showing its raw (unconverted) reading.
  static getActiveSelection(): number {
    return SegmentComposer.selections[SegmentComposer.activeIndex] ?? -1;
  }

  static getSegmentText(index: number): string {
    const segment = SegmentComposer.segments[index];
    if (!segment) {
      return '';
    }
    const selection = SegmentComposer.selections[index] ?? -1;
    if (selection < 0) {
      return segment.key;
    }
    return segment.candidates[selection] ?? segment.key;
  }

  static getAssembled(): string {
    return SegmentComposer.segments.map((_segment, i) => SegmentComposer.getSegmentText(i)).join('');
  }

  // Character offset range [start, end) of the active segment within the assembled string.
  static getActiveRange(): [number, number] {
    let start = 0;
    for (let i = 0; i < SegmentComposer.activeIndex; i += 1) {
      start += SegmentComposer.getSegmentText(i).length;
    }
    return [start, start + SegmentComposer.getSegmentText(SegmentComposer.activeIndex).length];
  }
}

export default SegmentComposer;
